import { InvoiceStatus } from "services/api/fleet/vehicles/invoices/types";

/*---------------------------------------------------------------
    INCOMING INVOICE
  ---------------------------------------------------------------*/

export interface InvoiceIncoming {
  id: string | null;
  invoiceNumber: string | null;
  contactSupplier: any | null;
  supplierName: string | null;
  supplierAddress: string | null;
  supplierOIB: string | null;
  supplierVAT: string | null;
  supplierIBAN: string | null;
  vehicle: any | null;
  invoiceDate: Date | null;
  dueDate: Date | null;
  durationDays: number | null;
  deliveryDate: Date | null;
  totalWithoutTax: number | null;
  totalTax: number | null;
  total: number | null;
  currencyId: string | null;
  exchangeRate: number | null;
  status: InvoiceStatus | null;
  incomingSourceId: string | null;
  /** Provider kroz kojeg je e-račun zaprimljen (moj_eracun/eposlovanje). */
  providerName: string | null;
  /** Id dokumenta kod providera. */
  providerInvoiceId: string | null;
  receivedFromProviderAt: Date | null;
  created: Date | null;
  updated: Date | null;
  /** Kad je odluka (odobren/odbijen) javljena provideru. */
  processStatusReportedAt: Date | null;
  rejectionReasonType: string | null;
  rejectionReason: string | null;
  /** Kad je odbijanje fiskalizirano kod Porezne uprave. */
  rejectionFiscalizedAt: Date | null;
  note: string | null;
  internalNote: string | null;
  expenseTypeId: string | null;
  isPaid: boolean;
  paidAt: Date | null;
  paymentMethodId: string | null;
  paymentReference: string | null;
  items: InvoiceIncomingItemRow[];
  documents: InvoiceIncomingDocument[];
  payments: InvoiceIncomingPayment[];
  totalPaid: number;
  remainingAmount: number;
}

export interface InvoiceIncomingRow {
  id: string;
  invoiceNumber: string | null;
  contactSupplier: any | null;
  supplierName: string | null;
  vehicle: any | null;
  invoiceDate: Date | null;
  dueDate: Date | null;
  totalWithoutTax: number | null;
  totalTax: number | null;
  total: number | null;
  status: InvoiceStatus | null;
  incomingSourceId: string | null;
  isPaid: boolean;
  documents: InvoiceIncomingDocument[];
  /** Stavke računa — prikazuju se u proširenom retku pretrage. */
  childRows?: InvoiceIncomingItemRow[];
  totalPaid: number;
  remainingAmount: number;
  /** "none" | "partial" | "full" — koliko je stavki pretvoreno u troškove. */
  allocationStatus: string | null;
  allocatedItemCount: number;
  itemCount: number;
}

/** Stanje raspodjele troškova računa; mora odgovarati backendu. */
export const InvoiceAllocationStatus = {
  NONE: "none",
  PARTIAL: "partial",
  FULL: "full",
} as const;

export interface InvoiceIncomingEditFormValues {
  id: string | null;
  vehicleId: string | null;
  contactSupplierId: string | null;
  supplierName: string | null;
  supplierAddress: string | null;
  supplierOIB: string | null;
  supplierVAT: string | null;
  supplierIBAN: string | null;
  invoiceNumber: string | null;
  invoiceDate: Date | null;
  dueDate: Date | null;
  durationDays: number | null;
  deliveryDate: Date | null;
  totalWithoutTax: number | null;
  totalTax: number | null;
  total: number | null;
  currencyId: string | null;
  exchangeRate: number | null;
  statusId: string | null;
  incomingSourceId: string | null;
  note: string | null;
  internalNote: string | null;
  expenseTypeId: string | null;
  paymentMethodId: string | null;
  paymentReference: string | null;
  items: InvoiceIncomingItemEditFormValues[];
  /** Datoteke priložene računu — popunjava ih učitavanje iz datoteke. */
  uploadIds?: string[];
}

export interface InvoiceIncomingItemRow {
  id: string;
  code: string | null;
  name: string | null;
  quantity: number | null;
  priceWithoutTax: number | null;
  priceWithTax: number | null;
  taxRate: number | null;
  discountTypeId: string | null;
  discountAmount: number | null;
  discountPercent: number | null;
  total: number | null;
  note: string | null;
  kpdCode: string | null;
  unitOfMeasureId: string | null;
  /** Je li iz stavke nastao trošak. */
  isAllocated?: boolean;
  deleted?: boolean;
}

export interface InvoiceIncomingItemEditFormValues {
  id: string;
  expenseTypeId: string | null;
  code: string | null;
  name: string | null;
  quantity: number | null;
  priceWithoutTax: number | null;
  priceWithTax: number | null;
  taxRate: number | null;
  discountTypeId: string | null;
  discountAmount: number | null;
  discountPercent: number | null;
  total: number | null;
  note: string | null;
  kpdCode: string | null;
  unitOfMeasureId: string | null;
  /** Vrsta stavke iz šifrarnika — null znači slobodan redak. Koristi je forma za unos stavke. */
  billingItemTypeId: string | null;
  billingItemTypeName?: string | null;
  deleted?: boolean;
}

export interface InvoiceIncomingPayment {
  id: string;
  amount: number;
  paidAt: Date;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  paymentReference: string | null;
  note: string | null;
  created: Date;
}

export interface InvoiceIncomingDocument {
  id: string;
  originalFileName: string;
  fileExtension: string;
  fileSizeKb: number;
  contentType: string;
  url: string;
  created: Date;
}

export interface InvoiceIncomingSearchFormValues {
  search: string | null;
  statusId: string | null;
  incomingSourceId: string | null;
  allocationStatus: string | null;
  invoiceDateFrom: Date | null;
  invoiceDateTo: Date | null;
  dueDateFrom: Date | null;
  dueDateTo: Date | null;
  totalMin: number | null;
  totalMax: number | null;
}

/** Razlog odbijanja e-računa — N: bez utjecaja na porez, U: utječe na porez, O: ostalo. */
export type InvoiceIncomingRejectionReasonType = "N" | "U" | "O";

export interface InvoiceIncomingRejection {
  reasonType: InvoiceIncomingRejectionReasonType;
  reason: string;
}

/** Kamo trošak iz stavke ide; mora odgovarati backendu. */
export const AllocationTargetType = {
  VEHICLE: "vehicle",
  COMPANY: "company",
} as const;

/** Jedan redak raspodjele stavke ulaznog računa — na vozilo ili na tvrtku; stavka ih može imati više. */
export interface InvoiceIncomingCostAllocation {
  /** "vehicle" ili "company". */
  targetTypeId: string;
  /** Postojeći trošak vozila; prazno znači novi redak koji još nije spremljen. */
  expenseId: string | null;
  invoiceIncomingItemId: string;
  vehicleId: string | null;
  /** Registracija (ili VIN) vozila — dolazi s poslužitelja da se ne dohvaća posebno. */
  vehicleName?: string | null;
  /** Vrsta troška iz šifrarnika — obavezna za oba cilja. */
  expenseTypeId: string | null;
  expenseTypeName?: string | null;
  /** Količina sa stavke koja otpada na ovo vozilo. */
  quantity: number | null;
  /** Poslovnica na koju trošak otpada; neobavezno. */
  officeId: string | null;
  /** Iznos bez PDV-a; prazno se na poslužitelju računa razmjerno količini. */
  amount: number | null;
}

/** Jedna datoteka u redu za čitanje. */
/** Pravokutnik na stranici u omjeru 0–1 prema njezinoj širini i visini. */
export interface InvoiceFieldBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  page: number;
}

export interface InvoiceIncomingQueueItem {
  uploadId: string;
  fileName: string;
  fileExtension: string;
  /** Adresa datoteke za prikaz uz obrazac. */
  url: string;
  /** Podaci pročitani s te datoteke; popunjava se tek kad dođe na red. */
  extraction?: InvoiceIncomingExtraction | null;
}

/** Odgovor na učitavanje datoteka — što je primljeno i što je odbijeno. */
export interface InvoiceIncomingDocumentQueue {
  queue: InvoiceIncomingQueueItem[];
  rejected: { fileName: string; reason: string }[];
}

/** Podaci pročitani s računa, spremni za popunjavanje obrasca. */
export interface InvoiceIncomingExtraction {
  uploadId: string;
  supplierName: string | null;
  supplierOIB: string | null;
  supplierAddress: string | null;
  supplierIBAN: string | null;
  contactSupplierId: string | null;
  invoiceNumber: string | null;
  invoiceDate: Date | null;
  dueDate: Date | null;
  currencyId: string | null;
  totalWithoutTax: number | null;
  totalTax: number | null;
  total: number | null;
  expenseTypeId: string | null;
  confidence: number | null;
  /** Što korisnik mora provjeriti prije spremanja. */
  warnings: string[];
  items: InvoiceIncomingExtractionItem[];
  /** Gdje na stranici koji podatak stoji; prazno kad čitanje nije dalo koordinate. */
  fieldLocations: Record<string, InvoiceFieldBox[]>;
  /** Pouzdanost po polju (0–1). */
  fieldConfidences: Record<string, number>;
  /** Zakret stranice prepoznat pri čitanju. */
  detectedRotation: number;
}

export interface InvoiceIncomingExtractionItem {
  code: string | null;
  name: string | null;
  quantity: number | null;
  priceWithoutTax: number | null;
  taxRate: number | null;
  discountPercent: number | null;
  total: number | null;
}
